import { TPSScores } from '@/types/tps.types';

/**
 * The 8 Jungian Cognitive Functions with behavioral indicators
 * Used for AI-driven cognitive function assessment
 */
export interface CognitiveFunctionDef {
  name: string;
  abbreviation: string;
  socionicsSymbol: string;
  axis: 'perception' | 'judgment';
  attitude: 'extraverted' | 'introverted';
  indicators: string[];
  description: string;
}

export const COGNITIVE_FUNCTIONS: Record<string, CognitiveFunctionDef> = {
  Ne: {
    name: 'Extraverted Intuition',
    abbreviation: 'Ne',
    socionicsSymbol: '⚫', // Intuition of Possibilities
    axis: 'perception',
    attitude: 'extraverted',
    indicators: [
      'Generating multiple possibilities and connections',
      'Getting excited by novel ideas and brainstorming',
      'Seeing patterns across unrelated domains',
      'Preferring to keep options open'
    ],
    description: 'Explores external possibilities, patterns, and potential connections between ideas'
  },
  Ni: {
    name: 'Introverted Intuition',
    abbreviation: 'Ni',
    socionicsSymbol: '🔺', // Intuition of Time
    axis: 'perception',
    attitude: 'introverted',
    indicators: [
      'Having sudden insights and "aha" moments',
      'Thinking in symbols and metaphors',
      'Focusing on a single vision or outcome',
      'Sensing how things will unfold over time'
    ],
    description: 'Synthesizes information into unified visions and foresees long-term implications'
  },
  Se: {
    name: 'Extraverted Sensing',
    abbreviation: 'Se',
    socionicsSymbol: '⬛', // Sensing of Force
    axis: 'perception',
    attitude: 'extraverted',
    indicators: [
      'Living fully in the present moment',
      'Responding quickly to immediate circumstances',
      'Enjoying physical experiences and action',
      'Noticing details in the environment'
    ],
    description: 'Engages directly with the physical world and responds to immediate sensory input'
  },
  Si: {
    name: 'Introverted Sensing',
    abbreviation: 'Si',
    socionicsSymbol: '⬜', // Sensing of Comfort
    axis: 'perception',
    attitude: 'introverted',
    indicators: [
      'Drawing on past experiences for guidance',
      'Valuing tradition and established methods',
      'Having detailed memories of personal experiences',
      'Preferring familiar, tried-and-true approaches'
    ],
    description: 'Stores and recalls detailed sensory impressions and compares present to past'
  },
  Te: {
    name: 'Extraverted Thinking',
    abbreviation: 'Te',
    socionicsSymbol: '⬆️', // Logic of Actions
    axis: 'judgment',
    attitude: 'extraverted',
    indicators: [
      'Organizing external environments efficiently',
      'Making decisions based on objective criteria',
      'Implementing systematic processes',
      'Measuring success through tangible results'
    ],
    description: 'Organizes the external world through logic, systems, and measurable outcomes'
  },
  Ti: {
    name: 'Introverted Thinking',
    abbreviation: 'Ti',
    socionicsSymbol: '⬇️', // Logic of Structures
    axis: 'judgment',
    attitude: 'introverted',
    indicators: [
      'Building internal frameworks and mental models',
      'Seeking logical consistency above all',
      'Analyzing systems to understand how they work',
      'Defining precise categories and distinctions'
    ],
    description: 'Constructs internal logical frameworks to understand and categorize information'
  },
  Fe: {
    name: 'Extraverted Feeling',
    abbreviation: 'Fe',
    socionicsSymbol: '❤️', // Ethics of Emotions
    axis: 'judgment',
    attitude: 'extraverted',
    indicators: [
      'Creating harmony in social environments',
      'Reading and responding to group emotions',
      'Prioritizing collective well-being',
      'Expressing emotions openly'
    ],
    description: 'Attunes to group emotional dynamics and works to maintain social harmony'
  },
  Fi: {
    name: 'Introverted Feeling',
    abbreviation: 'Fi',
    socionicsSymbol: '💜', // Ethics of Relations
    axis: 'judgment',
    attitude: 'introverted',
    indicators: [
      'Having strong personal values and convictions',
      'Evaluating situations based on internal ethics',
      'Deep empathy through personal identification',
      'Prioritizing authenticity over conformity'
    ],
    description: 'Evaluates information based on deeply held personal values and authenticity'
  }
};

/**
 * Function pairs used for scenario-based disambiguation
 */
export const FUNCTION_PAIRS = [
  { functions: ['Ne', 'Ni'], category: 'intuition', question: 'perception-intuitive' },
  { functions: ['Se', 'Si'], category: 'sensing', question: 'perception-sensing' },
  { functions: ['Te', 'Ti'], category: 'thinking', question: 'judgment-thinking' },
  { functions: ['Fe', 'Fi'], category: 'feeling', question: 'judgment-feeling' }
];

/**
 * MBTI type to function stack mapping
 */
export const MBTI_FUNCTION_STACKS: Record<string, { dom: string; aux: string; tert: string; inf: string }> = {
  'INTJ': { dom: 'Ni', aux: 'Te', tert: 'Fi', inf: 'Se' },
  'INTP': { dom: 'Ti', aux: 'Ne', tert: 'Si', inf: 'Fe' },
  'ENTJ': { dom: 'Te', aux: 'Ni', tert: 'Se', inf: 'Fi' },
  'ENTP': { dom: 'Ne', aux: 'Ti', tert: 'Fe', inf: 'Si' },
  'INFJ': { dom: 'Ni', aux: 'Fe', tert: 'Ti', inf: 'Se' },
  'INFP': { dom: 'Fi', aux: 'Ne', tert: 'Si', inf: 'Te' },
  'ENFJ': { dom: 'Fe', aux: 'Ni', tert: 'Se', inf: 'Ti' },
  'ENFP': { dom: 'Ne', aux: 'Fi', tert: 'Te', inf: 'Si' },
  'ISTJ': { dom: 'Si', aux: 'Te', tert: 'Fi', inf: 'Ne' },
  'ISFJ': { dom: 'Si', aux: 'Fe', tert: 'Ti', inf: 'Ne' },
  'ESTJ': { dom: 'Te', aux: 'Si', tert: 'Ne', inf: 'Fi' },
  'ESFJ': { dom: 'Fe', aux: 'Si', tert: 'Ne', inf: 'Ti' },
  'ISTP': { dom: 'Ti', aux: 'Se', tert: 'Ni', inf: 'Fe' },
  'ISFP': { dom: 'Fi', aux: 'Se', tert: 'Ni', inf: 'Te' },
  'ESTP': { dom: 'Se', aux: 'Ti', tert: 'Fe', inf: 'Ni' },
  'ESFP': { dom: 'Se', aux: 'Fi', tert: 'Te', inf: 'Ni' }
};

/**
 * Socionics type mapping from MBTI with function perspective
 */
export const MBTI_TO_SOCIONICS_FUNCTIONS: Record<string, { 
  socionicsType: string; 
  functionOrder: string[];
}> = {
  'INTJ': { socionicsType: 'INTp (ILI)', functionOrder: ['Ni', 'Te', 'Fi', 'Se'] },
  'INTP': { socionicsType: 'INTj (LII)', functionOrder: ['Ti', 'Ne', 'Si', 'Fe'] },
  'ENTJ': { socionicsType: 'ENTj (LIE)', functionOrder: ['Te', 'Ni', 'Se', 'Fi'] },
  'ENTP': { socionicsType: 'ENTp (ILE)', functionOrder: ['Ne', 'Ti', 'Fe', 'Si'] },
  'INFJ': { socionicsType: 'INFp (IEI)', functionOrder: ['Ni', 'Fe', 'Ti', 'Se'] },
  'INFP': { socionicsType: 'INFj (EII)', functionOrder: ['Fi', 'Ne', 'Si', 'Te'] },
  'ENFJ': { socionicsType: 'ENFj (EIE)', functionOrder: ['Fe', 'Ni', 'Se', 'Ti'] },
  'ENFP': { socionicsType: 'ENFp (IEE)', functionOrder: ['Ne', 'Fi', 'Te', 'Si'] },
  'ISTJ': { socionicsType: 'ISTj (LSI)', functionOrder: ['Si', 'Te', 'Fi', 'Ne'] },
  'ISFJ': { socionicsType: 'ISFj (ESI)', functionOrder: ['Si', 'Fe', 'Ti', 'Ne'] },
  'ESTJ': { socionicsType: 'ESTj (LSE)', functionOrder: ['Te', 'Si', 'Ne', 'Fi'] },
  'ESFJ': { socionicsType: 'ESFj (ESE)', functionOrder: ['Fe', 'Si', 'Ne', 'Ti'] },
  'ISTP': { socionicsType: 'ISTp (SLI)', functionOrder: ['Ti', 'Se', 'Ni', 'Fe'] },
  'ISFP': { socionicsType: 'ISFp (SEI)', functionOrder: ['Fi', 'Se', 'Ni', 'Te'] },
  'ESTP': { socionicsType: 'ESTp (SLE)', functionOrder: ['Se', 'Ti', 'Fe', 'Ni'] },
  'ESFP': { socionicsType: 'ESFp (SEE)', functionOrder: ['Se', 'Fi', 'Te', 'Ni'] }
};

export interface FunctionStrengths {
  Ne: number;
  Ni: number;
  Te: number;
  Ti: number;
  Fe: number;
  Fi: number;
  Se: number;
  Si: number;
}

/**
 * Derive the function stack from raw strength scores
 */
export function deriveFunctionStack(strengths: FunctionStrengths): {
  dominant: { func: string; score: number };
  auxiliary: { func: string; score: number };
  tertiary: { func: string; score: number };
  inferior: { func: string; score: number };
} {
  // Sort functions by strength
  const sorted = Object.entries(strengths)
    .sort(([, a], [, b]) => b - a);
  
  // Ensure valid cognitive function stack (alternating attitudes)
  const dominant = sorted[0];
  const dominantFunc = COGNITIVE_FUNCTIONS[dominant[0]];
  
  // Find auxiliary with opposite attitude
  const auxiliary = sorted.find(([func]) => {
    const f = COGNITIVE_FUNCTIONS[func];
    return f.attitude !== dominantFunc.attitude && f.axis !== dominantFunc.axis;
  }) || sorted[1];
  
  // Tertiary has opposite attitude to auxiliary
  const tertiary = sorted.find(([func]) => {
    const f = COGNITIVE_FUNCTIONS[func];
    return f.attitude !== COGNITIVE_FUNCTIONS[auxiliary[0]].attitude && 
           func !== dominant[0] && func !== auxiliary[0];
  }) || sorted[2];
  
  // Inferior is opposite of dominant
  const oppositeMap: Record<string, string> = {
    'Ne': 'Si', 'Ni': 'Se', 'Se': 'Ni', 'Si': 'Ne',
    'Te': 'Fi', 'Ti': 'Fe', 'Fe': 'Ti', 'Fi': 'Te'
  };
  const inferiorFunc = oppositeMap[dominant[0]];
  const inferior: [string, number] = [inferiorFunc, strengths[inferiorFunc as keyof FunctionStrengths]];
  
  return {
    dominant: { func: dominant[0], score: dominant[1] },
    auxiliary: { func: auxiliary[0], score: auxiliary[1] },
    tertiary: { func: tertiary[0], score: tertiary[1] },
    inferior: { func: inferior[0], score: inferior[1] }
  };
}

/**
 * Infer MBTI type from function stack
 */
export function inferMBTIFromStack(dominant: string, auxiliary: string): string | null {
  for (const [mbti, stack] of Object.entries(MBTI_FUNCTION_STACKS)) {
    if (stack.dom === dominant && stack.aux === auxiliary) {
      return mbti;
    }
  }
  return null;
}

/**
 * Infer Socionics type from function stack
 */
export function inferSocionicsFromStack(dominant: string, auxiliary: string): string | null {
  const mbti = inferMBTIFromStack(dominant, auxiliary);
  if (mbti && MBTI_TO_SOCIONICS_FUNCTIONS[mbti]) {
    return MBTI_TO_SOCIONICS_FUNCTIONS[mbti].socionicsType;
  }
  return null;
}
